import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import { Response, ErrorResponse, RequestConfig } from '../api-Utils';
import { clearSessionProfile } from 'shared/utils/sessionProfileStore';
import { fetchSessionContext, extractProfileUserId } from '../sessionContext';
import useToggle from 'shared/hooks/use-toggle';
import { getTenantIdFromWindowLocation, isTenantUuid } from 'shared/utils/tenantRoute';
import { resolveRegistryTenantId } from '../resolveRegistryTenantId';
import { getLastLoginTenantId } from 'shared/utils/lastTenantLogin';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';

const useAuthenticationService = () => {
  const [isLoading, toggleLoading] = useToggle();

  const mapApiResponse = (response, fallbackMessage = 'Request failed') => {
    const payload = response?.data;
    if (payload?.code === '0') {
      return Response('0', payload.message || fallbackMessage, payload.data ?? {});
    }
    return ErrorResponse(payload?.message || fallbackMessage, response?.status);
  };

  /** Prefer registry UUID; fall back to route tenant name (backend resolves either). */
  const resolveTenantIdForPasswordReset = () => {
    const fromSession = getRegistryTenantIdFromSession();
    if (fromSession && isTenantUuid(fromSession)) return fromSession;
    const fromLastLogin = getLastLoginTenantId();
    if (fromLastLogin && isTenantUuid(fromLastLogin)) return fromLastLogin;
    const fromRoute = getTenantIdFromWindowLocation();
    if (fromRoute) return fromRoute;
    return resolveRegistryTenantId() || undefined;
  };

  const signIn = async (email, phoneNumber, password) => {
    try {
      toggleLoading();
      const requestBody = { password };
      if (email && email.trim() !== '') {
        requestBody.email = email;
      }
      if (phoneNumber && phoneNumber.trim() !== '') {
        requestBody.phoneNumber = phoneNumber;
      }

      const requestConfig = RequestConfig(
        endpoints.authentication.signIn,
        {},
        requestBody,
        'POST'
      );

      const response = await axiosInstance(requestConfig);

      if (response.status === 200) {
        const profile = await fetchSessionContext();
        if (!extractProfileUserId(profile)) {
          return ErrorResponse(
            'Login succeeded but session context could not be loaded. Ensure the API is reachable (local: Spring Boot on :8067 + restart React dev server).',
            response.status
          );
        }
        return Response('0', response.data.message || 'Authentication successful', {});
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        'Unable to reach the server. Check API URL and network.';
      return ErrorResponse(msg, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const signUp = async (userData) => {
    try {
      toggleLoading();
      const tenantId =
        userData.tenantId ??
        getTenantIdFromWindowLocation() ??
        process.env.REACT_APP_TENANT_ID;
      const payload = {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        confirmedPassword: userData.confirmedPassword ?? userData.confirmPassword,
        phoneNumber: userData.phoneNumber,
        role: userData.role,
        companyId: userData.companyId,
        companyName: userData.companyName,
        tenantId: tenantId || undefined,
      };
      const requestConfig = RequestConfig(
        endpoints.users.signUp,
        {},
        payload,
        'POST'
      );

      const response = await axiosInstance(requestConfig);

      if (response.status === 200) {
        return Response('0', 'User registered successfully', response.data);
      } else {
        return ErrorResponse(response.data?.message, response.status);
      }
    } catch (error) {
      return ErrorResponse(error.response?.data?.message || error.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const logout = async (reason = 'user-logout') => {
    try {
      toggleLoading();
      try {
        await axiosInstance(
          RequestConfig(
            endpoints.authentication.signOut,
            { reason: reason || 'user-logout' },
            {},
            'POST'
          )
        );
      } catch {
        /* unreachable server: HttpOnly cookies may remain until they expire */
      }
      clearSessionProfile();
      return Response('0', 'User logged out successfully', {});
    } catch (error) {
      clearSessionProfile();
      return ErrorResponse(error.response?.data?.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const requestPasswordResetService = async (email) => {
    try {
      toggleLoading();
      const tenantId = resolveTenantIdForPasswordReset();
      const requestConfig = RequestConfig(
        endpoints.users.resetPassword,
        {},
        { email, tenantId },
        'POST'
      );

      const response = await axiosInstance(requestConfig);
      return mapApiResponse(response, 'Reset code sent to your email');
    } catch (error) {
      return ErrorResponse(error.response?.data?.message || error.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const verifyResetCodeService = async (email, code) => {
    try {
      toggleLoading();
      const requestConfig = RequestConfig(
        endpoints.authentication.verifyResetCode,
        {},
        { email, code },
        'POST'
      );

      const response = await axiosInstance(requestConfig);
      return mapApiResponse(response, 'Code verified successfully');
    } catch (error) {
      return ErrorResponse(error.response?.data?.message || error.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const resetPasswordService = async (email, newPassword, confirmPassword) => {
    try {
      toggleLoading();
      const tenantId = resolveTenantIdForPasswordReset();
      const requestConfig = RequestConfig(
        endpoints.users.newPassword,
        {},
        { email, password: newPassword, confirmPassword, tenantId },
        'POST'
      );

      const response = await axiosInstance(requestConfig);
      return mapApiResponse(response, 'Password changed successfully');
    } catch (error) {
      return ErrorResponse(error.response?.data?.message || error.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  return {
    signIn,
    signUp,
    logout,
    requestPasswordResetService,
    verifyResetCodeService,
    resetPasswordService,
    isLoading,
  };
};

export default useAuthenticationService;
